import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  IconSearch,
  IconFilter,
  IconRefresh,
  IconCheck,
  IconTrash,
  IconPencil,
  IconChevronUp,
  IconChevronDown,
  IconX
} from "@tabler/icons-react";
import { useDispatch, useSelector } from "react-redux";
import { fetchOrders, searchAndFilterOrdersAdmin, deleteOrdersAdmin, clearDeleteError } from "../../redux/slices/ordersSlice";
import { useAdminLayout } from "../../context/adminLayoutUseContext";
import ErrorFallback from "../../components/common/ErrorFallback";
import LoadingFallback from "../../components/common/LoadingFallback";

// Constants
const ORDER_STATUS = {
  ORDERING: 'Ordering',
  ORDER_PLACED: 'Order Placed',
  PENDING: 'Pending',
  SHIPPED: 'Shipped',
  OUT_FOR_DELIVERY: 'Out for Delivery',
  DELIVERED: 'Delivered',
  CANCELLED: 'Cancelled',
  REFUNDED: 'Refunded'
};

const PAYMENT_STATUS = {
  PENDING: 'Pending',
  COMPLETED: 'Completed',
  FAILED: 'Failed'
};

// Reusable Components
function IconButton({ children, onClick }) {
  return (
    <button onClick={onClick} className="p-1 hover:bg-gray-100 rounded transition">
      {children}
    </button>
  );
}

function StatusCell({ status, positiveLabel }) {
  const isPositive = status?.toLowerCase() === positiveLabel.toLowerCase();
  return (
    <div className="inline-flex items-center gap-1">
      {isPositive && <IconCheck className="text-green-500" size={16} />}
      <span>{status}</span>
    </div>
  );
}

// Filter Modal Component
function FilterModal({ isOpen, onClose, filters, setFilters, onApply }) {
  const [localFilters, setLocalFilters] = useState({
    orderStatus: [],
    paymentStatus: []
  });

  useEffect(() => {
    if (isOpen) {
      setLocalFilters({
        orderStatus: filters.orderStatus || [],
        paymentStatus: filters.paymentStatus || []
      });
    }
  }, [isOpen, filters]);

  const toggleOrderStatus = (status) => {
    setLocalFilters(prev => ({
      ...prev,
      orderStatus: prev.orderStatus.includes(status)
        ? prev.orderStatus.filter(s => s !== status)
        : [...prev.orderStatus, status]
    }));
  };

  const togglePaymentStatus = (status) => {
    setLocalFilters(prev => ({
      ...prev,
      paymentStatus: prev.paymentStatus.includes(status)
        ? prev.paymentStatus.filter(s => s !== status)
        : [...prev.paymentStatus, status]
    }));
  };

  const handleApply = () => {
    setFilters({
      ...filters,
      orderStatus: localFilters.orderStatus,
      paymentStatus: localFilters.paymentStatus
    });
    onApply();
    onClose();
  };

  const handleReset = () => {
    setLocalFilters({
      orderStatus: [],
      paymentStatus: []
    });
    setFilters({
      ...filters,
      orderStatus: [],
      paymentStatus: []
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white w-[500px] max-w-full rounded-md p-6 relative">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-500 hover:text-gray-700"
        >
          <IconX size={20} />
        </button>
        <h2 className="text-lg font-semibold text-[#0B0C52] mb-4">Filter Options</h2>

        <div className="mb-6">
          <h3 className="text-md font-semibold mb-2">Order Status</h3>
          <div className="grid grid-cols-2 gap-2">
            {Object.values(ORDER_STATUS).map(status => (
              <label key={status} className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={localFilters.orderStatus.includes(status)}
                  onChange={() => toggleOrderStatus(status)}
                  className="rounded border-gray-300 text-[#0B0C52] focus:ring-[#0B0C52]"
                />
                <span>{status}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="mb-6">
          <h3 className="text-md font-semibold mb-2">Payment Status</h3>
          <div className="grid grid-cols-2 gap-2">
            {Object.values(PAYMENT_STATUS).map(status => (
              <label key={status} className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={localFilters.paymentStatus.includes(status)}
                  onChange={() => togglePaymentStatus(status)}
                  className="rounded border-gray-300 text-[#0B0C52] focus:ring-[#0B0C52]"
                />
                <span>{status}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="flex justify-end gap-4">
          <button
            onClick={handleReset}
            className="bg-gray-300 text-gray-700 px-4 py-2 rounded hover:opacity-80"
          >
            Reset All
          </button>
          <button
            onClick={handleApply}
            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
          >
            Apply Filters
          </button>
        </div>
      </div>
    </div>
  );
}

// Delete Order Modal Component
function DeleteOrderModal({ items, onClose, onDelete, isLoading, error }) {
  const isBulkDelete = Array.isArray(items);
  const count = isBulkDelete ? items.length : 1;
  let message;

  if (isBulkDelete) {
    message = `Are you sure you want to permanently delete these ${count} selected orders? This action cannot be undone.`;
  } else {
    message = `Are you sure you want to permanently delete order #${items?.order_number || items?.order_uid}? This action cannot be undone.`;
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white w-[500px] max-w-full rounded-md p-6 relative">
        <button onClick={onClose} className="absolute top-3 right-3 text-gray-500 hover:text-gray-700" disabled={isLoading}>
          <IconX size={20} />
        </button>
        <h2 className="text-lg font-semibold text-red-600 mb-4 flex items-center gap-2">
          <IconTrash size={20} />
          Delete {count} Order(s)
        </h2>
        <p className="text-sm text-gray-600 mb-4">{message}</p>

        {error && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-800 text-sm">
            <strong>Could not delete order(s):</strong> {error}
          </div>
        )}

        <div className="flex justify-end gap-4 mt-6">
          <button
            onClick={onDelete}
            className="bg-red-500 text-white px-4 py-2 rounded hover:opacity-90 disabled:bg-red-300"
            disabled={isLoading}
          >
            {isLoading ? 'Deleting...' : 'Delete'}
          </button>
          <button
            onClick={onClose}
            className="bg-gray-300 text-gray-700 px-4 py-2 rounded hover:opacity-80"
            disabled={isLoading}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// Add Order Modal
function AddOrderModal({ onClose }) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white w-[700px] max-w-full rounded-md p-6 relative">
        <button onClick={onClose} className="absolute top-3 right-3 text-gray-500 hover:text-gray-700">
          <IconX size={20} />
        </button>
        <h2 className="text-lg font-semibold text-[#0B0C52] mb-4">Add New Order</h2>
        <div className="flex gap-4">
          <button
            className="bg-[#0B0C52] text-white px-4 py-2 rounded hover:opacity-90"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// Main Component
function OrderManagement() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { loading, error, data: allOrders = [], isSearching, deleteLoading, deleteError } = useSelector((state) => state.orders);

  // State management
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [modalType, setModalType] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const { setLayoutConfig } = useAdminLayout();

  const [filters, setFilters] = useState({
    orderStatus: [],
    paymentStatus: []
  });

  const pageSize = 10;

  useEffect(() => {
    setLayoutConfig({
      title: "Orders Management",
      showBackButton: false,
      scrollable: false,
    });
  }, [setLayoutConfig]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const payload = {
        search_term: searchTerm,
        order_status: filters.orderStatus,
        payment_status: filters.paymentStatus
      };
      if (searchTerm || filters.orderStatus.length > 0 || filters.paymentStatus.length > 0) {
        dispatch(searchAndFilterOrdersAdmin(payload));
      } else {
        dispatch(fetchOrders());
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm, filters, dispatch]);

  const totalCount = allOrders.length;
  const totalPages = Math.ceil(totalCount / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const currentOrders = allOrders.slice(startIndex, endIndex);
  const fromCount = startIndex + 1;
  const toCount = endIndex > totalCount ? totalCount : endIndex;

  const handleNextPage = () => currentPage < totalPages && setCurrentPage(prev => prev + 1);
  const handlePrevPage = () => currentPage > 1 && setCurrentPage(prev => prev - 1);

  const handleRefresh = () => {
    setSearchTerm("");
    setFilters({ orderStatus: [], paymentStatus: [] });
    setCurrentPage(1);
  };

  const handleApplyFilters = () => {
    setCurrentPage(1);
  };

  const toggleOrderSelection = (orderUid) => {
    setSelectedOrders(prev =>
      prev.includes(orderUid)
        ? prev.filter(uid => uid !== orderUid)
        : [...prev, orderUid]
    );
  };

  const selectAllOrders = () => {
    setSelectedOrders(prev =>
      prev.length === currentOrders.length
        ? []
        : currentOrders.map(order => order.order_uid)
    );
  };

  const openModal = (type, order = null) => {
    setSelectedOrder(order);
    setModalType(type);
  };

  const closeModal = () => {
    setSelectedOrder(null);
    setModalType(null);
    if (deleteError) {
      dispatch(clearDeleteError());
    }
  };

  const handleDeleteOrders = async () => {
    const uidsToDelete = Array.isArray(selectedOrder)
      ? selectedOrder
      : [selectedOrder.order_uid];

    if (!uidsToDelete || uidsToDelete.length === 0) return;

    try {
      await dispatch(deleteOrdersAdmin(uidsToDelete)).unwrap();

      // This part only runs if the dispatch is successful
      closeModal();
      setSelectedOrders([]);
    } catch (err) {

    }
  };

  if (loading && !isSearching) {
    return <LoadingFallback />;
  }
  if (error) {
    return <ErrorFallback error={error} onRetry={() => dispatch(fetchOrders())} />;
  }

  return (
    <main className="flex-1 bg-[#FFFFFF] text-gray-800 p-4 rounded">
      <div className="rounded-md bg-[#FFFFFF] py-4">
        {/* Toolbar Row */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          {selectedOrders.length > 0 ? (
            <div className="flex flex-wrap items-center justify-between gap-2 mb-4 w-full">
              <div className="flex items-center gap-4">
                <span className="text-sm text-gray-700 whitespace-nowrap">
                  {selectedOrders.length} selected
                </span>
                <div className="relative ml-1">
                  <input
                    type="text"
                    placeholder="Search By Order ID Or Customer Name"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="border-2 border-[#463BC9] rounded text-sm px-2 py-1 pl-8 focus:outline-none focus:border-[3px] peer"
                  />
                  <IconSearch size={16} className="absolute left-2 top-2 text-gray-400 peer-focus:text-[#463BC9]" />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  className="text-red-500 hover:text-red-700 flex items-center gap-1 text-sm px-3 py-1 rounded hover:bg-red-50"
                  onClick={() => openModal("DELETE", selectedOrders)}
                >
                  <IconTrash size={16} />
                  Delete
                </button>
                <button
                  className="text-gray-500 hover:text-gray-700 text-sm px-3 py-1 rounded hover:bg-gray-100"
                  onClick={() => setSelectedOrders([])}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="relative ml-1">
                <input
                  type="text"
                  placeholder="Search By Order ID Or Customer Name"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="border-2 border-[#463BC9] rounded text-sm px-2 py-1 pl-8 focus:outline-none focus:border-[3px] peer"
                />
                <IconSearch size={16} className="absolute left-2 top-2 text-gray-400 peer-focus:text-[#463BC9]" />
              </div>
              <span className="text-sm text-gray-700 ml-auto mr-1">
                {fromCount} - {toCount} of {totalCount}
              </span>
              <button onClick={handlePrevPage} className="p-1 hover:bg-gray-100 rounded">
                <IconChevronUp size={18} />
              </button>
              <button onClick={handleNextPage} className="p-1 hover:bg-gray-100 rounded">
                <IconChevronDown size={18} />
              </button>
              <IconButton onClick={() => setIsFilterModalOpen(true)}>
                <IconFilter size={18} />
              </IconButton>
              <IconButton onClick={handleRefresh}>
                <IconRefresh size={18} />
              </IconButton>
            </>
          )}
        </div>

        {/* Table */}
        <div className="overflow-x-auto h-[calc(100vh-200px)]">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-[#F9F9FF] z-20 shadow-md">
              <tr className="bg-[#F9F9FF] text-[#120E43]">
                <th className="px-4 py-2 w-10">
                  <input
                    type="checkbox"
                    checked={selectedOrders.length > 0 && selectedOrders.length === currentOrders.length}
                    onChange={selectAllOrders}
                    ref={(input) => {
                      if (input) {
                        input.indeterminate =
                          selectedOrders.length > 0 &&
                          selectedOrders.length < currentOrders.length;
                      }
                    }}
                  />
                </th>
                <th className="px-4 py-2 text-left">Order ID</th>
                <th className="px-4 py-2 text-left">Customer Name</th>
                <th className="px-4 py-2 text-left">Order Status</th>
                <th className="px-4 py-2 text-left">Payment Status</th>
                <th className="px-4 py-2 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentOrders.map((order) => (
                <tr key={order.order_uid} className="border-b border-gray-200 hover:bg-gray-50">
                  <td className="px-4 py-2">
                    <input
                      type="checkbox"
                      checked={selectedOrders.includes(order.order_uid)}
                      onChange={() => toggleOrderSelection(order.order_uid)}
                    />
                  </td>
                  <td className="px-4 py-2 font-mono">#{order.order_number}</td>
                  <td className="px-4 py-2">{order.customer_full_name}</td>
                  <td className="px-4 py-2">
                    <StatusCell status={order.status} positiveLabel="Delivered" />
                  </td>
                  <td className="px-4 py-2">
                    <StatusCell status={order.payment_status} positiveLabel="Completed" />
                  </td>
                  <td className="px-4 py-2">
                    <div className="flex items-center gap-2">
                      <button
                        className="text-blue-600 hover:text-blue-800 flex items-center gap-1"
                        onClick={() => navigate(`/admin/orders/${order.order_uid}`)}
                      >
                        <IconPencil size={16} />
                      </button>
                      <button
                        className="text-red-500 hover:text-red-700 flex items-center gap-1"
                        onClick={() => openModal("DELETE", order)}
                      >
                        <IconTrash size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {currentOrders.length === 0 && (
                <tr>
                  <td colSpan="6" className="text-center py-4 text-gray-500">
                    {isSearching ? 'No orders match your search criteria' : 'No orders found'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal Invocation */}
      {modalType === "DELETE" && selectedOrder && (
        <DeleteOrderModal
          items={selectedOrder}
          onClose={closeModal}
          onDelete={handleDeleteOrders}
          isLoading={deleteLoading}
          error={deleteError}
        />
      )}

      <FilterModal
        isOpen={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        filters={filters}
        setFilters={setFilters}
        onApply={handleApplyFilters}
      />
    </main>
  );
}

export default OrderManagement;